import { useEffect, useState } from "react";
import { api, fmtBytes, fmtNum } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Mail, Send, Trash2, RefreshCw, Copy, AlertTriangle } from "lucide-react";

const usd = (n) => `$${(n || 0).toFixed(n >= 1 ? 2 : 4)}`;

export default function TeamPanel() {
  const { user } = useAuth();
  const [invites, setInvites] = useState([]);
  const [users, setUsers] = useState([]);
  const [emailConfigured, setEmailConfigured] = useState(true);
  const [form, setForm] = useState({ email: "", plan: "free" });
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState("");

  const load = async () => {
    try {
      const [i, u] = await Promise.all([api.listInvites(), api.adminUsers()]);
      setInvites(i.invites);
      setEmailConfigured(i.email_configured);
      setUsers(u.users);
    } catch (e) { /* non-owner */ }
  };

  useEffect(() => { load(); }, []);

  if (user?.role !== "owner") return null;

  const sendInvite = async () => {
    setBusy(true);
    try {
      const r = await api.createInvite({ email: form.email.trim().toLowerCase(), plan: form.plan });
      setForm({ email: "", plan: "free" });
      setLastLink(r.invite_link || "");
      if (r.email_sent) toast.success(`Invite emailed to ${r.email}`);
      else toast.warning("Invite created, but the email did not send — copy the link below");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create invite");
    } finally { setBusy(false); }
  };

  const resend = async (id) => {
    try {
      const r = await api.resendInvite(id);
      setLastLink(r.invite_link || "");
      toast[r.email_sent ? "success" : "warning"](r.email_sent ? "Invite re-sent" : "New link generated (email failed)");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not resend"); }
  };

  const revoke = async (id) => {
    try { await api.deleteInvite(id); toast.success("Invite revoked"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke"); }
  };

  const changePlan = async (id, plan) => {
    try { await api.setUserPlan(id, { plan, quota_overrides: {} }); toast.success(`Plan set to ${plan}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not change plan"); }
  };

  const removeUser = async (id, email) => {
    if (!window.confirm(`Delete ${email} and all of their crawls, datasets and files? This cannot be undone.`)) return;
    try { await api.deleteUser(id); toast.success(`${email} removed`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete user"); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Link copied"); };

  return (
    <div className="rounded-lg border border-border bg-[#0B0F18] p-6 space-y-5" data-testid="team-panel">
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-cyan-400" />
        <h3 className="font-display font-bold text-base">Team &amp; Invites</h3>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-slate-600">
          {users.length} account{users.length === 1 ? "" : "s"}
        </span>
      </div>
      <p className="text-xs text-slate-500 -mt-2">
        Invite people by email instead of opening public sign-ups. Each invited account is its own tenant —
        they never see your crawls, datasets or API keys.
      </p>

      {!emailConfigured && (
        <p className="flex items-start gap-2 text-[11px] font-mono text-amber-300 border border-amber-500/40 bg-amber-500/10 rounded-md px-3 py-2">
          <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
          Email sending is not configured (RESEND_API_KEY missing) — invites still work, share the link manually.
        </p>
      )}

      <div className="grid sm:grid-cols-[1fr_150px_auto] gap-3 items-end">
        <div className="space-y-1.5">
          <Label className="text-[11px] text-slate-400 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Invite by email</Label>
          <Input data-testid="invite-email-input" type="email" value={form.email} placeholder="teammate@company.com"
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className="bg-[#0E1420] border-border font-mono text-sm" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] text-slate-400">Plan</Label>
          <Select value={form.plan} onValueChange={(v) => setForm((f) => ({ ...f, plan: v }))}>
            <SelectTrigger data-testid="invite-plan-select" className="bg-[#0E1420] border-border font-mono text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="pro">Pro</SelectItem>
              <SelectItem value="unlimited">Unlimited</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="send-invite-button" onClick={sendInvite} disabled={busy || !form.email.includes("@")}
          className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold disabled:opacity-40">
          <Send className="h-3.5 w-3.5 mr-1.5" /> Send invite
        </Button>
      </div>

      {lastLink && (
        <div className="flex items-center gap-2 rounded-md border border-border bg-[#04060A] px-3 py-2">
          <p data-testid="invite-link" className="font-mono text-[11px] text-cyan-300 truncate flex-1">{lastLink}</p>
          <Button size="sm" variant="ghost" data-testid="copy-invite-link" onClick={() => copy(lastLink)}
            className="h-7 text-slate-400 hover:text-cyan-300"><Copy className="h-3.5 w-3.5" /></Button>
        </div>
      )}

      <div className="space-y-2" data-testid="invite-list">
        <p className="text-[10px] font-mono uppercase tracking-wider text-slate-600">Invitations</p>
        {invites.length === 0 && <p className="text-xs font-mono text-slate-600">No invitations yet</p>}
        {invites.map((i) => (
          <div key={i.id} data-testid={`invite-row-${i.id}`}
            className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-[#0E1420] px-3 py-2">
            <span className="font-mono text-xs text-slate-200">{i.email}</span>
            <span className="px-1.5 py-0.5 rounded font-mono text-[9px] uppercase border border-slate-600 text-slate-400">{i.plan}</span>
            <span className={`px-1.5 py-0.5 rounded font-mono text-[9px] uppercase border ${
              i.accepted ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                : i.expired ? "border-rose-500/40 text-rose-300 bg-rose-500/10"
                : "border-amber-500/40 text-amber-300 bg-amber-500/10"}`}>
              {i.accepted ? "accepted" : i.expired ? "expired" : "pending"}
            </span>
            {!i.email_sent && !i.accepted && (
              <span className="font-mono text-[9px] text-amber-400/80">email not delivered</span>
            )}
            <div className="ml-auto flex items-center gap-1">
              {!i.accepted && (
                <Button size="sm" variant="ghost" data-testid={`resend-invite-${i.id}`} onClick={() => resend(i.id)}
                  className="h-7 text-slate-400 hover:text-cyan-300"><RefreshCw className="h-3.5 w-3.5" /></Button>
              )}
              <Button size="sm" variant="ghost" data-testid={`revoke-invite-${i.id}`} onClick={() => revoke(i.id)}
                className="h-7 text-slate-400 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-2" data-testid="tenant-list">
        <p className="text-[10px] font-mono uppercase tracking-wider text-slate-600">Accounts &amp; usage this month</p>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[10px] font-mono uppercase tracking-wider text-slate-600">
                <th className="py-2 pr-4">Email</th><th className="py-2 pr-4">Plan</th>
                <th className="py-2 pr-4">Pages</th><th className="py-2 pr-4">Tokens</th>
                <th className="py-2 pr-4">AI spend</th><th className="py-2 pr-4">Storage</th><th className="py-2"></th>
              </tr>
            </thead>
            <tbody className="font-mono text-xs">
              {users.map((u) => (
                <tr key={u.id} className="border-t border-border/60" data-testid={`tenant-row-${u.id}`}>
                  <td className="py-2 pr-4 text-slate-200">
                    {u.email}
                    {u.role === "owner" && <span className="ml-1.5 text-[9px] uppercase text-cyan-400">owner</span>}
                  </td>
                  <td className="py-2 pr-4">
                    <Select value={u.plan} onValueChange={(v) => changePlan(u.id, v)}>
                      <SelectTrigger data-testid={`plan-select-${u.id}`}
                        className="h-7 w-[120px] bg-[#0E1420] border-border font-mono text-[11px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="pro">Pro</SelectItem>
                        <SelectItem value="unlimited">Unlimited</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-2 pr-4">{fmtNum(u.usage.pages)}</td>
                  <td className="py-2 pr-4">{fmtNum(u.usage.in_tokens + u.usage.out_tokens)}</td>
                  <td className="py-2 pr-4 text-emerald-300">{usd(u.usage.cost_usd)}</td>
                  <td className="py-2 pr-4">{fmtBytes(u.usage.storage_bytes)}</td>
                  <td className="py-2">
                    {u.role !== "owner" && (
                      <Button size="sm" variant="ghost" data-testid={`delete-user-${u.id}`}
                        onClick={() => removeUser(u.id, u.email)}
                        className="h-7 text-slate-500 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
